import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft, Camera, CheckCircle, QrCode, RefreshCw, Search, User, XCircle } from 'lucide-react';

const scannerElementId = 'admin-checkin-qr-reader';

export default function AdminCheckin() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('home');
  const [search, setSearch] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const scannerRef = useRef(null);
  const scanLockedRef = useRef(false);
  const scanSessionRef = useRef(0);
  const mountedRef = useRef(false);

  const load = useCallback(() => {
    apiGet('/api/guests')
      .then(r => setGuests(r.data.guests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getCameraErrorMessage = (err) => {
    if (!err) return 'Ukendt kamerafejl';
    if (typeof err === 'string') return err;
    return err.message || err.name || String(err);
  };

  const cleanupScanner = useCallback(async ({ resetLock = false } = {}) => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    let cleanupError = null;

    if (scanner) {
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch (err) {
        cleanupError = err;
      }

      try {
        await scanner.clear();
      } catch (err) {
        cleanupError = cleanupError || err;
      }
    }

    if (resetLock) scanLockedRef.current = false;
    if (mountedRef.current) {
      setCameraActive(false);
      setCameraStarting(false);
    }

    return cleanupError;
  }, []);

  const stopCamera = useCallback(async ({ resetLock = true, invalidateSession = true } = {}) => {
    if (invalidateSession) scanSessionRef.current += 1;
    return cleanupScanner({ resetLock });
  }, [cleanupScanner]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    const interval = setInterval(load, 15000);
    return () => {
      mountedRef.current = false;
      scanSessionRef.current += 1;
      clearInterval(interval);
      void cleanupScanner({ resetLock: true });
    };
  }, [cleanupScanner, load]);

  const pickCamera = (cameras) => {
    const rearCamera = cameras.find(camera => /back|rear|environment|bag|bagside|ryg/i.test(camera.label || ''));
    return rearCamera || cameras[0];
  };

  const isActiveScanSession = useCallback((sessionId) => (
    mountedRef.current && scanSessionRef.current === sessionId
  ), []);

  const checkedIn = guests.filter(g => g.checkin_status === 'checked_in').length;

  const manualResults = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return [];
    return guests
      .filter(g =>
        g.full_name?.toLowerCase().includes(trimmed) ||
        (g.display_email || g.private_email || g.corporate_email || '').toLowerCase().includes(trimmed)
      )
      .slice(0, 12);
  }, [guests, search]);

  const submitScanValue = async (value, sessionId) => {
    const trimmedValue = (value || '').trim();
    if (!isActiveScanSession(sessionId)) return;

    setScanFeedback({ type: 'info', message: 'QR-kode fundet...' });
    setScanResult(null);

    const cleanupError = await cleanupScanner({ resetLock: false });
    if (!isActiveScanSession(sessionId)) return;
    if (cleanupError) {
      scanLockedRef.current = false;
      setScanFeedback({ type: 'error', message: `Scanner kunne ikke stoppes korrekt: ${getCameraErrorMessage(cleanupError)}` });
      return;
    }

    if (!trimmedValue) {
      scanLockedRef.current = false;
      setScanFeedback({ type: 'error', message: 'QR-koden er tom eller ugyldig.' });
      return;
    }

    try {
      let body;
      try {
        JSON.parse(trimmedValue);
        body = { qr_data: trimmedValue };
      } catch {
        body = { guest_id: trimmedValue };
      }

      const res = await apiPost('/api/checkin/scan', body);
      if (!isActiveScanSession(sessionId)) return;
      const guest = res.data?.guest || {};
      setScanResult({
        name: guest.full_name || guest.name || 'Gæst',
        email: guest.display_email || guest.private_email || guest.corporate_email || '',
        status: 'Tjekket ind',
        time: new Date().toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }),
      });
      setScanFeedback({ type: 'success', message: 'QR-kode scannet.' });
      load();
    } catch (err) {
      if (!isActiveScanSession(sessionId)) return;
      scanLockedRef.current = false;
      setScanFeedback({ type: 'error', message: err.response?.data?.detail || 'Check-in mislykkedes.' });
    }
  };

  const startCamera = async () => {
    const sessionId = scanSessionRef.current + 1;
    scanSessionRef.current = sessionId;
    scanLockedRef.current = false;

    try {
      setMode('scan');
      setScanFeedback({ type: 'info', message: 'Åbner kamera...' });
      setScanResult(null);
      setCameraStarting(true);
      await cleanupScanner({ resetLock: false });
      if (!isActiveScanSession(sessionId)) return;

      setCameraActive(true);
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (!isActiveScanSession(sessionId)) return;

      const scannerElement = document.getElementById(scannerElementId);
      if (!scannerElement) throw new Error('Scannerfeltet blev ikke fundet.');

      const cameras = await Html5Qrcode.getCameras();
      if (!cameras.length) throw new Error('Ingen kameraer fundet på denne enhed.');

      const selectedCamera = pickCamera(cameras);
      const scanner = new Html5Qrcode(scannerElementId, { verbose: false });
      scannerRef.current = scanner;

      await scanner.start(
        selectedCamera.id,
        {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            const boxSize = Math.max(260, Math.min(Math.floor(minEdge * 0.84), 420));
            return { width: boxSize, height: boxSize };
          },
        },
        decodedText => {
          if (!isActiveScanSession(sessionId)) return;
          if (scanLockedRef.current) return;
          scanLockedRef.current = true;
          try {
            if (scannerRef.current?.isScanning) scannerRef.current.pause(true);
          } catch {}
          void submitScanValue(decodedText, sessionId);
        },
        () => {
          // Per-frame decode failures are expected while the camera is scanning.
        }
      );

      if (!isActiveScanSession(sessionId)) return;
      setScanFeedback({ type: 'info', message: 'Scanner QR-kode...' });
      setCameraStarting(false);
    } catch (err) {
      if (!isActiveScanSession(sessionId)) return;
      await cleanupScanner({ resetLock: true });
      if (isActiveScanSession(sessionId)) {
        setScanFeedback({ type: 'error', message: `Kunne ikke åbne kameraet: ${getCameraErrorMessage(err)}` });
      }
    }
  };

  const closeScanner = async () => {
    await stopCamera({ resetLock: true, invalidateSession: true });
    setScanFeedback(null);
    setScanResult(null);
    setMode('home');
  };

  const handleScanNext = async () => {
    await stopCamera({ resetLock: true, invalidateSession: true });
    setScanFeedback(null);
    setScanResult(null);
    await startCamera();
  };

  const handleCheckin = async (guestId) => {
    try {
      await apiPost(`/api/checkin/${guestId}`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Check-in mislykkedes');
    }
  };

  const handleCheckout = async (guestId) => {
    try {
      await apiPost(`/api/checkout/${guestId}`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fortryd check-in mislykkedes');
    }
  };

  const startButtonLabel = scanFeedback?.type === 'error'
    ? 'Prøv igen'
    : cameraStarting
      ? 'Starter...'
      : cameraActive
        ? 'Scanner...'
        : 'Start kamera';

  return (
    <div className="animate-fade-in min-w-0" data-testid="admin-checkin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Check-in</h1>
          <p className="text-sm text-slate-400 mt-1">{checkedIn}/{guests.length} tjekket ind</p>
        </div>
        <Button variant="outline" onClick={load} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-checkin-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {mode === 'home' && (
        <div className="mx-auto max-w-md rounded-2xl border border-slate-700/50 bg-slate-800/40 p-5 sm:p-6 text-center">
          <h2 className="text-xl font-semibold text-slate-50 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Check-in</h2>
          <Button
            onClick={() => { void startCamera(); }}
            disabled={loading || cameraStarting}
            className="w-full min-h-14 bg-blue-600 hover:bg-blue-500 text-white text-base"
            data-testid="start-scan-mode-btn"
          >
            <Camera className="w-5 h-5 mr-2" /> Scan QR-kode
          </Button>
          <div className="py-4 text-xs uppercase tracking-[0.2em] text-slate-500">eller</div>
          <Button
            variant="outline"
            onClick={() => { void stopCamera({ resetLock: true, invalidateSession: true }); setMode('manual'); }}
            className="w-full min-h-14 bg-slate-900/60 border-slate-700 text-slate-200 hover:bg-slate-800 text-base"
            data-testid="start-manual-mode-btn"
          >
            <Search className="w-5 h-5 mr-2" /> Manuel søgning
          </Button>
        </div>
      )}

      {mode === 'scan' && (
        <div className="mx-auto max-w-3xl space-y-4" data-testid="scan-mode">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Scan QR-kode</h2>
              <p className="text-sm text-slate-400">Hold gæstens QR-kode inde i rammen.</p>
            </div>
            <Button variant="outline" onClick={() => { void closeScanner(); }} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
              <XCircle className="w-4 h-4 mr-2" /> Luk scanner
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-3 sm:p-4">
            {!scanResult && (
              <div
                id={scannerElementId}
                className={`${cameraActive ? 'block' : 'hidden'} w-full min-h-[min(68vh,430px)] sm:min-h-[560px] overflow-hidden rounded-xl border border-blue-500/40 bg-black text-slate-200 [&_button]:rounded-md [&_button]:bg-slate-800 [&_button]:px-3 [&_button]:py-2 [&_button]:text-sm [&_button]:text-slate-100 [&_select]:bg-slate-900 [&_select]:text-slate-100 [&_video]:!w-full [&_video]:!min-h-[min(68vh,430px)] sm:[&_video]:!min-h-[560px] [&_video]:!object-cover`}
                data-testid="camera-scan-preview"
              />
            )}

            {!cameraActive && !scanResult && (
              <div className="min-h-[320px] rounded-xl border border-dashed border-slate-700 bg-slate-950/60 flex items-center justify-center p-6 text-center">
                <div>
                  <QrCode className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                  <p className="text-sm text-slate-400">{cameraStarting ? 'Starter kamera...' : 'Kameraet er ikke aktivt.'}</p>
                </div>
              </div>
            )}

            {scanResult && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 sm:p-6 text-center" data-testid="scan-result">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center">
                  <CheckCircle className="w-9 h-9" />
                </div>
                <p className="text-xs uppercase tracking-[0.22em] text-emerald-300">Tjekket ind</p>
                <p className="text-2xl font-semibold text-slate-50 mt-2 break-words" style={{ fontFamily: "'Outfit', sans-serif" }}>{scanResult.name}</p>
                {scanResult.email && <p className="text-xs text-slate-400 font-mono break-all mt-1">{scanResult.email}</p>}
                <p className="text-base text-slate-200 mt-3">{scanResult.status} kl. {scanResult.time}</p>
              </div>
            )}
          </div>

          {scanFeedback && (
            <p className={`flex items-center gap-2 text-sm ${scanFeedback.type === 'success' ? 'text-emerald-400' : scanFeedback.type === 'error' ? 'text-red-400' : 'text-blue-300'}`} data-testid="scan-feedback">
              {scanFeedback.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              <span className="min-w-0 break-words">{scanFeedback.message}</span>
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {scanResult ? (
              <Button onClick={() => { void handleScanNext(); }} className="w-full min-h-12 bg-blue-600 hover:bg-blue-500 text-white" data-testid="scan-next-btn">
                <QrCode className="w-4 h-4 mr-2" /> Scan næste
              </Button>
            ) : (
              <Button onClick={() => { void startCamera(); }} disabled={cameraStarting || cameraActive} className="w-full min-h-12 bg-blue-600 hover:bg-blue-500 text-white">
                <Camera className="w-4 h-4 mr-2" /> {startButtonLabel}
              </Button>
            )}
            <Button variant="outline" onClick={() => { void closeScanner(); }} className="w-full min-h-12 bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
              Luk scanner
            </Button>
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div className="mx-auto max-w-3xl space-y-4" data-testid="manual-mode">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Manuel søgning</h2>
              <p className="text-sm text-slate-400">Søg på navn eller e-mail.</p>
            </div>
            <Button variant="outline" onClick={() => { setMode('home'); setSearch(''); }} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 mr-2" /> Tilbage
            </Button>
          </div>

          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Søg efter gæst..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-slate-900/70 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-xl pl-10 pr-3 py-3 outline-none text-base"
              data-testid="checkin-search"
            />
          </div>

          {loading ? (
            <p className="text-slate-400">Indlæser...</p>
          ) : !search.trim() ? (
            <p className="text-center py-8 text-slate-500 text-sm">Skriv et navn eller en e-mail for at finde gæsten.</p>
          ) : manualResults.length === 0 ? (
            <p className="text-center py-8 text-slate-500 text-sm">Ingen gæster fundet.</p>
          ) : (
            <div className="space-y-2 min-w-0">
              {manualResults.map(g => (
                <div key={g.id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border transition-all ${
                  g.checkin_status === 'checked_in' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800/40 border-slate-700/50'
                }`} data-testid={`checkin-row-${g.id}`}>
                  <div className="flex items-center gap-3 min-w-0 w-full">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                      g.checkin_status === 'checked_in' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {g.checkin_status === 'checked_in' ? <CheckCircle className="w-5 h-5" /> : <User className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-200 truncate">{g.full_name}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{g.display_email || g.private_email || g.corporate_email}</p>
                      {g.checkin_at && <p className="text-xs text-slate-500 mt-1">Tjekket ind kl. {new Date(g.checkin_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}</p>}
                    </div>
                  </div>
                  {g.checkin_status === 'checked_in' ? (
                    <Button variant="outline" size="sm" onClick={() => handleCheckout(g.id)}
                      className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-400 hover:text-red-400 hover:border-red-500/30" data-testid={`checkout-${g.id}`}>
                      <XCircle className="w-4 h-4 mr-2" /> Fortryd
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => handleCheckin(g.id)}
                      className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`checkin-${g.id}`}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Tjek ind
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
